// Spec: specs/stats/sidebar/filters_tab/respondent_uids/functional_requirements.md

import React from 'react'
import { Map } from 'immutable'
import { render, unmountComponentAtNode } from 'react-dom'
import { act } from 'react-dom/test-utils'

// The gate under test is the only thing this file asserts, so every heavy child is stubbed down to a
// marker element — the panel body, the advanced editor and the basic groups are covered elsewhere.
jest.mock('@aytm/app/stats/page/sidebar/filters/FiltersUids', () => ({
  __esModule: true,
  default: ({ uids, uidsTruncated }) => (
    <div data-testid="uids-stub" data-count={uids.length} data-truncated={String(uidsTruncated)} />
  )
}))

jest.mock('@aytm/app/stats/page/sidebar/filters/FiltersAdvanced', () => ({
  __esModule: true,
  default: () => <div data-testid="advanced-stub" />
}))

jest.mock('@aytm/app/stats/page/sidebar/filters/FiltersGroup', () => ({
  __esModule: true,
  default: () => <div data-testid="group-stub" />
}))

jest.mock('@aytm/lib/rfd-ui/components/basic/text-popup', () => ({
  __esModule: true,
  default: ({ children }) => <div>{children}</div>
}))

jest.mock('primereact/button', () => ({
  __esModule: true,
  Button: ({ label, onClick }) => (
    <button type="button" onClick={onClick}>
      {label}
    </button>
  )
}))

jest.mock('@aytm/lib/layout/container-expander', () => ({
  __esModule: true,
  default: () => ({ update: () => {}, destroy: () => {} })
}))

jest.mock('@aytm/app/stats/lib/schema-rules', () => ({
  __esModule: true,
  default: { flatten: () => ({}) }
}))

jest.mock('@aytm/app/stats/lib/basic-filters', () => ({
  __esModule: true,
  default: class BasicFiltersStub {
    convertToBasic() {
      return {}
    }
  }
}))

import FiltersPanel from './FiltersPanel'

// `errors` drives parserResultPatternError, which is what forces the basic parse to null.
const parserResult = (errors = []) => ({
  expression: { status: { errors, codebooks: {} }, tree: {} }
})

const makeParser = (errors = []) => ({ analyzeString: () => parserResult(errors) })

let container = null

beforeEach(() => {
  container = document.createElement('div')
  document.body.appendChild(container)
})

afterEach(() => {
  unmountComponentAtNode(container)
  container.remove()
  container = null
})

// the panel scrolls and measures its scroll root on mount; jsdom elements carry neither
const scrollRoot = () => Object.assign(document.createElement('div'), { scroll: () => {} })

const baseProps = {
  data: {},
  features: { advanced: { available: true, disabled: false, hidden: false, onPackage: true } },
  locale: Map(),
  filters: [],
  tags: {},
  codeBooks: [],
  clusters: [],
  surveySchema: {},
  disabled: false,
  isAdmin: true,
  filtersMode: 'advanced',
  filtersPattern: 'q1a1',
  uids: [1, 2, 3],
  uidsTruncated: false,
  onChange: () => {},
  onScrollToQuestion: () => {},
  onSwitchMode: () => {},
  onShowHiddenGroup: () => {},
  onLoad: () => {},
  onDNDStart: () => {},
  onDNDEnd: () => {},
  restoreQuestion: () => {},
  getApiKey: () => {}
}

const mount = (props = {}) => {
  const { parserErrors = [], ...rest } = props
  act(() => {
    render(<FiltersPanel {...baseProps} root={scrollRoot()} parser={makeParser(parserErrors)} {...rest} />, container)
  })
}

const uidsSurface = () => container.querySelector('[data-testid="uids-stub"]')

describe('FiltersPanel — Show UUIDs visibility gate', () => {
  it('renders the surface for an internal viewer with the Advanced editor showing and a filter applied', () => {
    mount()

    expect(uidsSurface()).not.toBeNull()
  })

  it('withholds it from a non-internal viewer', () => {
    mount({ isAdmin: false })

    expect(uidsSurface()).toBeNull()
  })

  it('withholds it while the panel is not interactive', () => {
    mount({ disabled: true })

    expect(uidsSurface()).toBeNull()
  })

  it('withholds it when no filter is applied', () => {
    mount({ filtersPattern: '' })

    expect(uidsSurface()).toBeNull()
  })

  it('withholds it while the Basic editor is showing', () => {
    mount({ filtersMode: 'basic' })

    expect(uidsSurface()).toBeNull()
  })

  it('renders it in Basic mode when the applied filter has no basic parse', () => {
    mount({ filtersMode: 'basic', parserErrors: [{ type: 'syntax' }] })

    expect(uidsSurface()).not.toBeNull()
  })

  it('passes the loaded uids straight through', () => {
    mount({ uids: [11, 22], uidsTruncated: true })

    expect(uidsSurface().dataset.count).toBe('2')
    expect(uidsSurface().dataset.truncated).toBe('true')
  })
})
